import {
  CheckCircle2,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
  Wallet,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { BUDGET_STATUS_LABELS } from "~/types/contabilidad";
import { formatDate } from "~/utils/helpers";

import type { Budget } from "~/types/contabilidad";

interface PresupuestoTableProps {
  budgets: Budget[];
  onOpen: (budget: Budget) => void;
  onApprove: (budget: Budget) => void;
  onReopen: (budget: Budget) => void;
  onDelete: (budget: Budget) => void;
}

export function PresupuestoTable({
  budgets,
  onOpen,
  onApprove,
  onReopen,
  onDelete,
}: PresupuestoTableProps) {
  if (budgets.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Wallet}
          title="No hay presupuestos"
          description="Crea un presupuesto para comenzar el control presupuestario."
        />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Empresa</TableHead>
          <TableHead>Año</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Cuentas</TableHead>
          <TableHead>Actualizado</TableHead>
          <TableHead className="w-[60px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {budgets.map((budget) => (
          <TableRow
            key={budget.id}
            className="cursor-pointer hover:bg-muted/30"
            onClick={() => onOpen(budget)}
          >
            <TableCell>{budget.company?.name || "—"}</TableCell>
            <TableCell className="font-mono">{budget.fiscal_year}</TableCell>
            <TableCell className="font-medium">{budget.name}</TableCell>
            <TableCell>
              <Badge
                variant={budget.status === "approved" ? "default" : "secondary"}
                className={
                  budget.status === "approved"
                    ? "bg-green-100 text-green-800 hover:bg-green-100"
                    : undefined
                }
              >
                {BUDGET_STATUS_LABELS[budget.status]}
              </Badge>
            </TableCell>
            <TableCell className="text-right font-mono">
              {budget.lines_count ?? 0}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDate(budget.updated_at)}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onOpen(budget)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Abrir
                  </DropdownMenuItem>
                  {budget.status === "draft" ? (
                    <DropdownMenuItem onClick={() => onApprove(budget)}>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Aprobar
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => onReopen(budget)}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reabrir
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => onDelete(budget)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
